/**
 * Flipped classroom (M5) — local-first mutations for the class session plan.
 *
 * They write to PowerSync → write-path (`db/sync.rs::session_module`), which
 * applies the authz (cohort manager: ManageAcademics with campus scoping or
 * lead teacher), the `academy.flipped_sessions` feature gate, and HARD
 * integrity (the session exists; the module belongs to the cohort's COURSE).
 * The denormalized cohort_id/organization_id/campus_id are server-authoritative
 * (filled in here for local optimism).
 */

import { eq, type PowerSyncDrizzleDB, session_module } from "@space/powersync";
import { nowISO } from "../../utils/date-helpers";
import { generateId } from "@fleet/utils";
import {
  patchRowVerified,
  type VerifiedWriteOptions,
  withScope,
} from "../../powersync/verified-write";
import { isPlanPhase, type PlanPhase } from "./types";

export interface SessionPlanContext {
  sessionId: string;
  cohortId: string;
  organizationId: string;
  campusId?: string | null;
}

/** Adds a module to a session's plan under a phase. Returns the created id. */
export async function addSessionModuleMutation(
  db: PowerSyncDrizzleDB,
  ctx: SessionPlanContext,
  params: { moduleId: string; phase: PlanPhase; position?: number; note?: string | null },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  const config = params.note ? JSON.stringify({ note: params.note }) : "{}";
  await db.insert(session_module).values({
    id,
    class_session_id: ctx.sessionId,
    module_id: params.moduleId,
    cohort_id: ctx.cohortId,
    organization_id: ctx.organizationId,
    campus_id: ctx.campusId ?? null,
    phase: params.phase,
    position: params.position ?? 0,
    config,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/**
 * `organizationId`: the org the caller ALREADY has in context
 * (`SessionPlanEditor` receives it from `ctx`/`props`, see `SessionPlanContext`
 * above). The class session plan is "cohort manager: ManageAcademics" (see
 * the file header) — a real organization capability.
 *
 * Changes the phase and/or position of a plan item.
 */
export async function setSessionModulePhaseMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  phase: PlanPhase,
  position?: number,
  organizationId?: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  if (!isPlanPhase(phase)) {
    return;
  }
  const patch: Record<string, unknown> = { phase };
  if (typeof position === "number") {
    patch.position = position;
  }
  await patchRowVerified(
    db,
    session_module,
    session_module.id,
    id,
    patch,
    withScope(options, [{ column: session_module.organization_id, value: organizationId }]),
  );
}

/** ⚠️ Dead code: no callers in `apps/web` (no drag-reorder yet). */
export async function moveSessionModuleMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  position: number,
): Promise<void> {
  await db
    .update(session_module)
    .set({ position, updated_at: nowISO() })
    .where(eq(session_module.id, id));
}

/**
 * Removes a module from the plan (soft-delete; restorable with "Undo").
 * `organizationId`: see note in `setSessionModulePhaseMutation`.
 */
export async function removeSessionModuleMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  organizationId?: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    session_module,
    session_module.id,
    id,
    { deleted_at: nowISO() },
    withScope(options, [{ column: session_module.organization_id, value: organizationId }]),
  );
}

/**
 * Restores a removed item (deleted_at → NULL). Write-path re-validates authz.
 * `organizationId`: see note in `setSessionModulePhaseMutation`.
 */
export async function restoreSessionModuleMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  organizationId?: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    session_module,
    session_module.id,
    id,
    { deleted_at: null },
    withScope(options, [{ column: session_module.organization_id, value: organizationId }]),
  );
}
